import { IconButton, IconButtonWithText } from '..';
import { useTranslation } from '../../../hooks/useTranslation';
import { useState, useCallback, useEffect } from 'react';
import { TermDto, PostTermsDto } from '../../../commonTypes/client.types';
import useHttpPostPaymentTerms from '../../../hooks/client/clientApi/useHttpPostPaymentTerms';

interface TermModalProps {
    onSave: (saveCallback: () => void) => void;
    terms: Partial<TermDto>[];
}

export const InvoiceTermModal = ({ onSave, terms: initialTerms }: TermModalProps) => {
    const { t } = useTranslation('invoice');
    const { execPost } = useHttpPostPaymentTerms();

    const [terms, setTerms] = useState<Partial<TermDto>[]>(initialTerms);

    const handleAddNew = () => {
        setTerms([...terms, { name: '', numberOfDays: 0 }]);
    };

    const handleChange = (index: number, field: keyof TermDto, value: string | number) => {
        const updated = [...terms];
        if (field === 'numberOfDays') {
            updated[index].numberOfDays = Number(value);
        } else if (field === 'name') {
            updated[index].name = String(value);
        }
        setTerms(updated);
    };

    const handleDelete = (index: number) => {
        const updated = terms.filter((_, i) => i !== index);
        setTerms(updated);
    };

    const handleSave = useCallback(() => {
        const newTerms: PostTermsDto[] = terms
            .filter(term => !term.id && term.name && term.numberOfDays !== undefined)
            .map(term => ({
                name: term.name!,
                numberOfDays: term.numberOfDays!,
            }));

        if (newTerms.length > 0) {
            execPost(newTerms);
        }
    }, [terms, execPost]);

    useEffect(() => {
        onSave(handleSave);
    }, [handleSave, onSave]);

    return (
        <div className="invoice-term">
            <div className="invoice-term__content-container">
                <div className="invoice-create__table-container">
                    <table className="invoice-create__table">
                        <thead>
                            <tr className="invoice-create__table-row">
                                <th className="invoice-create__table-head-cell">
                                    <div className="invoice-create__header-cell">
                                        <span className="invoice-create__bold-text">{t('termName')}</span>
                                    </div>
                                </th>
                                <th className="invoice-create__table-head-cell">
                                    <div className="invoice-create__header-cell">
                                        <span className="invoice-create__bold-text">{t('termDay')}</span>
                                    </div>
                                </th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {terms.map((term, index) => (
                                <tr key={index} className="invoice-create__table-row">
                                    <td className="invoice-create__table-cell">
                                        <input
                                            type="text"
                                            value={term.name}
                                            placeholder="Enter Term Name"
                                            onChange={e => handleChange(index, 'name', e.target.value)}
                                            className="invoice-create__currency-select paragraph-3"
                                        />
                                    </td>
                                    <td className="invoice-create__table-cell">
                                        <input
                                            type="number"
                                            value={term.numberOfDays}
                                            placeholder="0"
                                            onChange={e => handleChange(index, 'numberOfDays', e.target.value)}
                                            className="invoice-create__currency-select paragraph-3"
                                        />
                                    </td>
                                    <td className="invoice-create__table-cell-actions">
                                        <div className="invoice-create__table-cell-action">
                                            <IconButton
                                                type="button"
                                                iconUrl="/svg/close-x-red.svg"
                                                variant="icon"
                                                iconAlt="delete"
                                                onClick={() => handleDelete(index)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div>
                    <IconButtonWithText
                        onClick={handleAddNew}
                        iconSrc="/svg/plus-blue-without-circle.svg"
                        iconAlt="add"
                        variant="text"
                        textColour="blue"
                        text={t('addNewTerm')}
                    />
                </div>
            </div>
        </div>
    );
};
